import { Button } from "@/components/ui/button";
import { PackageFeatureList } from "@/components/PackageFeatureList";
import { Eyebrow } from "@/components/typography/Eyebrow";
import { Heading } from "@/components/typography/Heading";
import { Text } from "@/components/typography/Text";
import { create as createRegistration } from "@/routes/register/info";
import { index as pricingIndex } from "@/routes/pricing";
import type { FrontendPackage } from "@/types/models";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-stroke-standard";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "@inertiajs/react";
import type { ReactElement } from "react";

const featuredPackageNodes = [100, 250, 500];

function FreeTrialCta({ className }: { className?: string }): ReactElement {
  return (
    <Link href={createRegistration()}>
      <Button
        size="lg"
        className={`bg-green-600 text-base font-semibold text-white hover:bg-green-500 ${className || ""}`}
      >
        ابدأ مجانًا الآن
      </Button>
    </Link>
  );
}

function PrePackageClarification({ freeNodesCount }: { freeNodesCount: number }): ReactElement {
  return (
    <div className="border-y border-gray-200 py-5">
      <div className="flex flex-col gap-5 text-center sm:flex-row sm:items-center sm:justify-between sm:text-right">
        <div className="min-w-0">
          <Heading level={3} size="subsection">
            جرّب المنصة مجانًا مع <span className="text-green-600">{freeNodesCount} فرد</span>
          </Heading>
          <Text size="small" className="mt-1">
            كل الباقات أدناه شراء رصيد لمرة واحدة.
          </Text>
          <div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-md border border-green-100 bg-green-50 px-3 py-1.5 text-sm font-bold text-green-800">
            <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} strokeWidth={2} className="shrink-0" />
            <span>لا يوجد اشتراك شهري</span>
          </div>
        </div>
        <div className="shrink-0">
          <FreeTrialCta />
        </div>
      </div>
    </div>
  );
}

interface LandingPricingProps {
  packages: FrontendPackage[];
  freeNodesCount: number;
}

function AllPackagesList({ packages }: Pick<LandingPricingProps, "packages">): ReactElement {
  return (
    <div className="mx-auto mt-8 max-w-3xl py-2">
      <div className="divide-y divide-gray-200">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-center sm:gap-10"
          >
            <div className="grid grid-cols-[1fr_auto] items-start gap-4 sm:contents">
              <div className="min-w-0 sm:order-1">
                <Heading level={4} size="title" className="text-gray-900">
                  {pkg.nodesFormatted} فرد
                </Heading>
                <Text size="helper" className="mt-1 text-gray-600">
                  رصيد يضاف لحسابك مباشرة بعد الدفع
                </Text>
              </div>

              <Link href={pricingIndex()} className="sm:order-3">
                <Button size="sm" className="min-w-28">
                  اشتر الآن
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:order-2 sm:flex sm:items-center sm:gap-8">
              <div>
                <Text size="helper" className="text-gray-500">
                  السعر
                </Text>
                <Text size="small" className="font-bold text-gray-900">
                  {pkg.priceFormatted} ر.س
                </Text>
                {pkg.originalPrice && (
                  <Text size="helper" className="text-gray-500">
                    <span className="line-through">{pkg.originalPrice} ر.س</span> · خصم {pkg.discountPercentage}٪
                  </Text>
                )}
              </div>
              <div>
                <Text size="helper" className="text-gray-500">
                  لكل فرد
                </Text>
                <Text size="small" className="font-bold text-green-700">
                  {pkg.pricePerNode} ر.س
                </Text>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LandingPricing({ packages, freeNodesCount }: LandingPricingProps): ReactElement {
  const featuredPackages = packages.filter((pkg) => featuredPackageNodes.includes(pkg.nodes));
  const compactPackages = packages.filter((pkg) => !featuredPackageNodes.includes(pkg.nodes));

  const assurances = [
    {
      title: "دفعة واحدة فقط",
      description: "تشحن رصيدك مرة واحدة وتستخدمه عند الحاجة، بلا التزامات أو اشتراكات.",
    },
    {
      title: "دفع إلكتروني موثوق",
      description: "بوابات دفع سعودية معتمدة مع تشفير كامل للبيانات.",
    },
    {
      title: "تفعيل فوري",
      description: "يضاف الرصيد مباشرة إلى حسابك بعد الدفع.",
    },
    {
      title: "فريق يفهم العائلات",
      description: "دعم فني ترافقك خطوة بخطوة لبناء شجرة العائلة.",
    },
  ];

  return (
    <section className="py-12 md:py-16">
      <div className="container mx-auto px-4">
        {/* Pricing Cards */}
        <div className="mx-auto mb-10 max-w-5xl">
          <PrePackageClarification freeNodesCount={freeNodesCount} />
        </div>

        <div className="mx-auto max-w-6xl">
          <div className="grid gap-6 lg:grid-cols-3">
            {featuredPackages.map((pkg) => {
              return (
                <article
                  key={pkg.id}
                  className="relative flex h-full flex-col rounded-3xl border border-gray-200 bg-white p-8 text-gray-900 transition duration-300 hover:-translate-y-1"
                >
                  <header className="mb-6 flex items-start justify-between">
                    <div>
                      <Eyebrow className="text-green-600">{pkg.nodesFormatted} فرد</Eyebrow>
                      <Heading level={3} size="metric" className="mt-2">
                        {pkg.priceFormatted} ر.س
                      </Heading>
                      {pkg.originalPrice && (
                        <Text size="helper" className="text-green-700">
                          <span className="line-through">{pkg.originalPrice} ر.س</span> · خصم {pkg.discountPercentage}٪
                        </Text>
                      )}
                    </div>
                    <div className="text-right text-green-600">
                      <Text size="helper" className="text-green-600">
                        السعر لكل فرد
                      </Text>
                      <Text size="small" className="font-semibold text-green-700">
                        {pkg.pricePerNode} ر.س
                      </Text>
                    </div>
                  </header>
                  <PackageFeatureList nodesFormatted={pkg.nodesFormatted} />
                  <Link href={pricingIndex()} className="mt-auto">
                    <Button
                      className="h-11 w-full bg-green-600 text-sm font-semibold text-white transition hover:bg-green-500"
                      size="lg"
                    >
                      اشتر الآن
                    </Button>
                  </Link>
                </article>
              );
            })}
          </div>
        </div>

        {compactPackages.length > 0 ? <AllPackagesList packages={compactPackages} /> : null}

        {/* Assurances */}
        <div className="mx-auto mt-16 max-w-5xl border-y border-gray-200 py-8 text-gray-900">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <Heading level={3} size="subsection">
                خدمة موثوقة من مؤسسة أوراق لتقنية المعلومات
              </Heading>
              <Text size="small" className="mt-2">
                سجل تجاري 1010744065، نخدم الأسر العربية منذ 2020 بعقود رسمية ومسار دفع آمن.
              </Text>
            </div>

            <ul className="grid gap-4 sm:grid-cols-2">
              {assurances.map((item) => (
                <li key={item.title} className="flex gap-3">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
                    <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} strokeWidth={2} />
                  </span>
                  <div>
                    <Heading level={4} size="title" className="text-gray-900">
                      {item.title}
                    </Heading>
                    <Text size="helper" className="mt-1 text-gray-600">
                      {item.description}
                    </Text>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
